import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useState, useEffect, useMemo } from 'react';
import { StaffPage } from '../components/staff/StaffChrome';
import { api } from '../services/apiService';
import { getAdminProductConfig } from '../config/productConfig';
import { getIcon } from '../services/iconRegistry';
import IconPicker from '../components/IconPicker';
import SearchableDropdown from '../components/SearchableDropdown';
import { DatePicker } from '../components/DatePicker';
import { ConfirmDialog } from '../components/finance/financeUi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LayoutList,
  Plus,
  Trash2,
  GripVertical,
  ChevronRight,
  Save,
  RotateCcw,
  AlertCircle,
  Check,
  Eye,
  ChevronDown,
  ChevronUp,
  X,
  Paintbrush,
  History,
  Smartphone,
  Globe,
  Bell,
  Shield,
  CalendarClock,
  ChevronsUpDown,
  Layers,
  Link2,
} from 'lucide-react';

interface VisibilityRule {
  type: 'feature' | 'date' | 'package';
  condition: 'has' | 'missing' | 'before' | 'after';
  value: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  displayOrder: number;
  roles: string[];
  nativeHidden?: boolean;
  menuStyle?: any;
  requiredPackageId?: string;
  badgeSource?: string;
  labels?: Record<string, string>;
  visibilityRules?: VisibilityRule[];
}

interface VersionHistoryEntry {
  version: number;
  groups: MenuGroup[];
  routeRegistry?: RouteEntry[];
  updatedAt: number;
  updatedBy: string;
}

interface MenuGroup {
  id: string;
  label: string;
  icon?: string;
  displayOrder: number;
  collapsible: boolean;
  defaultExpanded: boolean;
  roles: string[];
  items?: MenuItem[];
  subgroups?: MenuGroup[];
}

interface PackageOption {
  id: string;
  name: string;
  productLine?: string;
}

interface RouteEntry {
  path: string;
  label: string;
  category?: string;
}

// Default route registry — auto-discovered from platform-app's App.tsx routes
const DEFAULT_ROUTE_REGISTRY: RouteEntry[] = [
  // Core
  { path: '/', label: 'Dashboard', category: 'Core' },
  { path: '/settings', label: 'Settings', category: 'Core' },
  { path: '/settings/notifications', label: 'Notification Settings', category: 'Core' },
  { path: '/settings/ai-images', label: 'AI Image Settings', category: 'Core' },
  { path: '/notifications', label: 'Notifications', category: 'Core' },
  { path: '/subscription-setup', label: 'Subscription Setup', category: 'Core' },
  { path: '/suspended', label: 'Suspended', category: 'Core' },
  { path: '/no-product', label: 'No Product', category: 'Core' },
  { path: '/support', label: 'Support', category: 'Core' },
  { path: '/contact', label: 'Contact', category: 'Core' },
  { path: '/affiliate-program', label: 'Affiliate Program', category: 'Core' },

  // Menu Hub (CafeMaster recipes/inventory)
  { path: '/menu-hub', label: 'Menu Hub', category: 'Menu Hub' },
  { path: '/menu-manager/new', label: 'New Menu Board', category: 'Menu Hub' },
  { path: '/menu-manager/print', label: 'Print Menu', category: 'Menu Hub' },
  { path: '/recipes', label: 'Recipes', category: 'Menu Hub' },
  { path: '/inventory', label: 'Inventory Hub', category: 'Menu Hub' },
  { path: '/inventory/ingredients', label: 'Ingredients', category: 'Menu Hub' },
  { path: '/inventory/supplies', label: 'Supplies', category: 'Menu Hub' },
  { path: '/inventory/stock-reports', label: 'Stock Reports', category: 'Menu Hub' },
  { path: '/inventory/order-lists', label: 'Order Lists', category: 'Menu Hub' },
  { path: '/inventory/units', label: 'Units', category: 'Menu Hub' },

  // Creator Studio
  { path: '/creator-studio', label: 'Creator Studio', category: 'Creator Studio' },
  { path: '/creator-studio/credits', label: 'Creator Credits', category: 'Creator Studio' },
  { path: '/video-library', label: 'Video Library', category: 'Creator Studio' },
  { path: '/mini-studio', label: 'Mini Menu Studio', category: 'Creator Studio' },

  // Team
  { path: '/users', label: 'Team Management', category: 'Team' },
  { path: '/schedule', label: 'Schedule', category: 'Team' },
  { path: '/routines', label: 'Daily Routines', category: 'Team' },
  { path: '/training', label: 'Training', category: 'Team' },
  { path: '/portal', label: 'Employee Portal', category: 'Team' },

  // POS - Sales
  { path: '/pos/dashboard', label: 'POS Dashboard', category: 'POS Sales' },
  { path: '/pos/terminal', label: 'POS Terminal', category: 'POS Sales' },
  { path: '/pos/orders', label: 'Orders', category: 'POS Sales' },
  { path: '/pos/returns', label: 'Returns', category: 'POS Sales' },
  { path: '/pos/payments', label: 'Payments', category: 'POS Sales' },
  { path: '/pos/customers', label: 'Customers', category: 'POS Sales' },

  // POS - Catalog
  { path: '/pos/products', label: 'Products', category: 'POS Catalog' },
  { path: '/pos/products/new', label: 'New Product', category: 'POS Catalog' },
  { path: '/pos/products/import-export', label: 'Import / Export', category: 'POS Catalog' },
  { path: '/pos/products/health', label: 'Catalog Health', category: 'POS Catalog' },
  { path: '/pos/categories', label: 'Categories', category: 'POS Catalog' },
  { path: '/pos/brands', label: 'Brands', category: 'POS Catalog' },
  { path: '/pos/modifier-groups', label: 'Modifier Groups', category: 'POS Catalog' },
  { path: '/pos/pricing-models', label: 'Pricing Models', category: 'POS Catalog' },
  { path: '/pos/taxes-fees', label: 'Taxes & Fees', category: 'POS Catalog' },
  { path: '/pos/unit-conversions', label: 'Unit Conversions', category: 'POS Catalog' },

  // POS - Stock
  { path: '/pos/stock', label: 'Stock Overview', category: 'POS Stock' },
  { path: '/pos/stock/grn-history', label: 'GRN History', category: 'POS Stock' },
  { path: '/pos/stock/adjustment-history', label: 'Adjustment History', category: 'POS Stock' },
  { path: '/pos/stock/health', label: 'Stock Health', category: 'POS Stock' },
  { path: '/pos/stock-inspector', label: 'Stock Inspector', category: 'POS Stock' },
  { path: '/pos/transfer-hub', label: 'Transfer Hub', category: 'POS Stock' },
  { path: '/pos/purchase-orders', label: 'Purchase Orders', category: 'POS Stock' },
  { path: '/pos/suppliers', label: 'Suppliers', category: 'POS Stock' },
  { path: '/pos/inventory', label: 'POS Inventory', category: 'POS Stock' },
  { path: '/pos/store-supplies/new', label: 'New Store Supply', category: 'POS Stock' },
  { path: '/pos/bulk-operations', label: 'Bulk Operations', category: 'POS Stock' },

  // POS - Locations
  { path: '/pos/locations', label: 'Locations', category: 'POS Locations' },
  { path: '/pos/locations/new', label: 'New Location', category: 'POS Locations' },

  // POS - Reports
  { path: '/pos/reports', label: 'Reports Hub', category: 'POS Reports' },
  { path: '/pos/sales-reports', label: 'Sales Reports', category: 'POS Reports' },
  { path: '/pos/reports/daily-paperwork', label: 'Daily Paperwork', category: 'POS Reports' },
  { path: '/pos/reports/cash-discounts', label: 'Cash Discounts', category: 'POS Reports' },
  { path: '/pos/reports/employee-discounts', label: 'Employee Discounts', category: 'POS Reports' },
  { path: '/pos/reports/category-performance', label: 'Category Performance', category: 'POS Reports' },
  { path: '/pos/reports/product-performance', label: 'Product Performance', category: 'POS Reports' },
  { path: '/pos/reports/customer-insights', label: 'Customer Insights', category: 'POS Reports' },
  { path: '/pos/reports/employee-activity', label: 'Employee Activity', category: 'POS Reports' },
  { path: '/pos/reports/employee-performance', label: 'Employee Performance', category: 'POS Reports' },
  { path: '/pos/reports/inventory', label: 'Inventory Report', category: 'POS Reports' },
  { path: '/pos/reports/loyalty', label: 'Loyalty Report', category: 'POS Reports' },
  { path: '/pos/reports/margin-analysis', label: 'Margin Analysis', category: 'POS Reports' },
  { path: '/pos/reports/payroll', label: 'Payroll', category: 'POS Reports' },
  { path: '/pos/reports/tax', label: 'Tax Report', category: 'POS Reports' },
  { path: '/pos/reports/tips', label: 'Tips Report', category: 'POS Reports' },
  { path: '/pos/reports/return-bin', label: 'Return Bin', category: 'POS Reports' },
  { path: '/pos/reports/cash-drawer', label: 'Cash Drawer', category: 'POS Reports' },

  // POS - Marketing
  { path: '/pos/promotions', label: 'Promotions', category: 'POS Marketing' },
  { path: '/pos/promotions/new', label: 'New Promotion', category: 'POS Marketing' },
  { path: '/pos/promotions/cash/new', label: 'New Cash Discount', category: 'POS Marketing' },
  { path: '/pos/marketing', label: 'Marketing', category: 'POS Marketing' },
  { path: '/pos/ecommerce', label: 'E-Commerce', category: 'POS Marketing' },
  { path: '/website-analytics', label: 'Website Analytics', category: 'POS Marketing' },
  { path: '/pos/b2b', label: 'B2B Wholesale', category: 'POS Marketing' },

  // POS - Loyalty
  { path: '/pos/loyalty', label: 'Loyalty Hub', category: 'POS Loyalty' },

  // POS - Compliance & Lottery
  { path: '/pos/compliance', label: 'Compliance', category: 'POS Compliance & Lottery' },
  { path: '/pos/lottery', label: 'Lottery', category: 'POS Compliance & Lottery' },
  { path: '/pos/gas', label: 'Gas & Fuel', category: 'POS Compliance & Lottery' },

  // POS - Retail Menu
  { path: '/pos/recipes', label: 'POS Recipes', category: 'POS Retail Menu' },
  { path: '/pos/retail-menu-manager', label: 'Retail Menu Manager', category: 'POS Retail Menu' },

  // POS - Configuration
  { path: '/pos/role-groups', label: 'Role Groups', category: 'POS Configuration' },
  { path: '/pos/access-management', label: 'Access Management', category: 'POS Configuration' },
  { path: '/pos/time-tracking', label: 'Time Tracking', category: 'POS Configuration' },
  { path: '/pos/button-layout', label: 'Button Layout', category: 'POS Configuration' },

  // Services (pkg_services)
  { path: '/services/calendar', label: 'Calendar', category: 'Services' },
  { path: '/services/tickets', label: 'Job tickets', category: 'Services' },
  { path: '/services/items', label: 'Service items', category: 'Services' },
  { path: '/services/rentals', label: 'Rentals', category: 'Services' },
  { path: '/services/settings', label: 'Services settings', category: 'Services' },

  // Management
  { path: '/management', label: 'Management Hub', category: 'Management' },
  { path: '/management/my-work', label: 'My Work', category: 'Management' },

  // Real Estate
  { path: '/properties', label: 'Properties', category: 'Real Estate' },
  { path: '/listings', label: 'Listings', category: 'Real Estate' },

  // Ruby Estate (platform-app pages/estate/EstateRoutes.tsx)
  { path: '/estate', label: 'Estate Home', category: 'Ruby Estate' },
  { path: '/estate/properties', label: 'Properties & Units', category: 'Ruby Estate' },
  { path: '/estate/residents', label: 'Residents', category: 'Ruby Estate' },
  { path: '/estate/leases', label: 'Leases', category: 'Ruby Estate' },
  { path: '/estate/listings?tab=applications', label: 'Applications', category: 'Ruby Estate' },
  { path: '/estate/rent', label: 'Rent & Payments', category: 'Ruby Estate' },
  { path: '/estate/work-orders', label: 'Work Orders', category: 'Ruby Estate' },
  { path: '/estate/vendors', label: 'Maintenance companies', category: 'Ruby Estate' },
  { path: '/estate/bills', label: 'Bills & Insurance', category: 'Ruby Estate' },
  { path: '/estate/listings', label: 'Ruby Estate Online', category: 'Ruby Estate' },
  { path: '/estate/payments', label: 'Payments setup', category: 'Ruby Estate' },

  // Super Admin
  { path: '/super-admin/dashboard', label: 'Admin Dashboard', category: 'Super Admin' },
  { path: '/super-admin/tenants', label: 'Tenants', category: 'Super Admin' },
  { path: '/super-admin/leads', label: 'Leads', category: 'Super Admin' },
  { path: '/super-admin/billing', label: 'Billing', category: 'Super Admin' },
  { path: '/super-admin/settings', label: 'Admin Settings', category: 'Super Admin' },
  { path: '/super-admin/support', label: 'Admin Support', category: 'Super Admin' },
  { path: '/super-admin/ai', label: 'AI Control Center', category: 'Super Admin' },
  { path: '/super-admin/docs', label: 'Documentation', category: 'Super Admin' },
  { path: '/super-admin/affiliates', label: 'Affiliates', category: 'Super Admin' },
  { path: '/super-admin/command-center', label: 'Command Center', category: 'Super Admin' },
];

// Display order for category sections in the route picker
const ROUTE_CATEGORY_ORDER = [
  'Core',
  'Menu Hub',
  'Creator Studio',
  'Team',
  'POS Sales',
  'POS Catalog',
  'POS Stock',
  'POS Locations',
  'POS Reports',
  'POS Marketing',
  'POS Loyalty',
  'POS Compliance & Lottery',
  'POS Retail Menu',
  'POS Configuration',
  'Services',
  'Management',
  'Real Estate',
  'Ruby Estate',
  'Super Admin',
  'In Use',
  'Other',
];

// Sortable wrappers — MUST be defined outside MenusPage to avoid remount on every render
const SortableItemRow = ({ item, index, totalItems, onUpdate, onRemove, onMove, renderItemRow }: {
  item: MenuItem; index: number; totalItems: number;
  onUpdate: (id: string, u: Partial<MenuItem>) => void;
  onRemove: (id: string) => void;
  onMove: (i: number, d: 'up' | 'down') => void;
  renderItemRow: (item: MenuItem, index: number, totalItems: number, onUpdate: any, onRemove: any, onMove: any, dragHandleProps: any) => React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : undefined };
  return (
    <div ref={setNodeRef} style={style}>
      {renderItemRow(item, index, totalItems, onUpdate, onRemove, onMove, { listeners, attributes })}
    </div>
  );
};

const SortableGroupCard = ({ group, index, renderContent }: {
  group: MenuGroup; index: number;
  renderContent: (dragHandleProps: { listeners: any; attributes: any }) => React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : undefined };
  return (
    <div ref={setNodeRef} style={style}>
      {renderContent({ listeners, attributes })}
    </div>
  );
};

// ========================
// Ruby-on-light building blocks for this page (custom controls only — no
// native select / checkbox / confirm). Defined outside MenusPage so they
// never remount the rows they live in.
// ========================
const RUBY_GRADIENT = 'linear-gradient(135deg, #e8365a, #DC143C 55%, #9F1239)';
const INPUT_CLASS =
  'w-full h-8 rounded-md border border-stone-300 bg-white px-2.5 text-[13px] text-stone-900 placeholder-stone-400 transition-colors hover:border-stone-400 focus:outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15';

const FieldLabel = ({ children, icon: Icon }: { children: React.ReactNode; icon?: React.FC<any> }) => (
  <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
    {Icon && <Icon size={12} className="text-stone-400" />}
    {children}
  </span>
);

const Toggle = ({ checked, onChange, label, hint }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className="flex items-center gap-3 h-8 text-left"
  >
    <span className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-[#DC143C]' : 'bg-stone-300'}`}>
      <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </span>
    <span>
      <span className="block text-[13px] font-medium text-stone-800">{label}</span>
      {hint && <span className="block text-[11px] text-stone-500">{hint}</span>}
    </span>
  </button>
);

const RoleChips = ({ roles, selected, onChange }: {
  roles: string[]; selected: string[]; onChange: (roles: string[]) => void;
}) => (
  <div className="flex flex-wrap gap-1.5">
    {roles.map(role => {
      const on = selected.includes(role);
      return (
        <button
          key={role}
          type="button"
          aria-pressed={on}
          onClick={() => onChange(on ? selected.filter(r => r !== role) : [...selected, role])}
          className={`h-8 px-3 rounded-full border text-[11px] font-medium capitalize inline-flex items-center gap-1.5 transition-colors ${
            on
              ? 'bg-rose-50 border-[#DC143C] text-[#9F1239]'
              : 'bg-white border-stone-300 text-stone-500 hover:border-stone-400 hover:text-stone-700'
          }`}
        >
          {on && <Check size={12} />}
          {role}
        </button>
      );
    })}
  </div>
);

function Segmented<T extends string>({ options, value, onChange }: {
  options: { value: T; label: string }[]; value: T; onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex flex-wrap rounded-md bg-stone-100 p-0.5 gap-0.5">
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`h-6 px-2.5 rounded text-[11px] font-medium transition-colors ${
            value === o.value ? 'bg-white text-[#DC143C] shadow-sm' : 'text-stone-500 hover:text-stone-800'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const IconButton = ({ active, onClick, title, children, badge }: {
  active?: boolean; onClick: () => void; title: string; children: React.ReactNode; badge?: number;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-pressed={active}
    className={`relative w-8 h-8 shrink-0 rounded-lg border flex items-center justify-center transition-colors ${
      active
        ? 'border-[#DC143C] text-[#DC143C] bg-rose-50'
        : 'border-stone-300 text-stone-500 bg-white hover:text-stone-900 hover:border-stone-400'
    }`}
  >
    {children}
    {!!badge && (
      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC143C] text-white text-[10px] font-bold flex items-center justify-center">
        {badge}
      </span>
    )}
  </button>
);

const AddButton = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-8 px-2.5 rounded-md border border-rose-200 bg-white text-[#DC143C] text-xs font-medium inline-flex items-center gap-1.5 hover:bg-rose-50 hover:border-[#DC143C] transition-colors"
  >
    <Plus size={13} />
    {children}
  </button>
);

const SectionHead = ({ title, count, action }: { title: string; count?: number; action?: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2">
      <span className="w-1 h-4 rounded-full" style={{ background: RUBY_GRADIENT }} />
      <p className="text-xs font-bold uppercase tracking-wider text-stone-700">{title}</p>
      {count !== undefined && (
        <span className="text-[11px] font-semibold text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">{count}</span>
      )}
    </div>
    {action}
  </div>
);

const PanelTitle = ({ icon: Icon, children }: { icon: React.FC<any>; children: React.ReactNode }) => (
  <div className="flex items-center gap-2 text-[13px] font-semibold text-stone-800">
    <span className="w-6 h-6 rounded-md bg-rose-50 text-[#DC143C] flex items-center justify-center">
      <Icon size={14} />
    </span>
    {children}
  </div>
);

/** Colour swatch that wraps the colour input so it never shows the native chrome. */
const ColorSwatchInput = ({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-stone-500 w-10">{label}</span>
    <label
      className="relative w-8 h-8 rounded-lg border border-stone-300 cursor-pointer shadow-inner hover:border-stone-400"
      style={{ backgroundColor: value }}
      title={`Pick ${label.toLowerCase()} colour`}
    >
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </label>
    <code className="text-xs font-mono text-stone-500 uppercase">{value}</code>
  </div>
);

/** Modal shell in the house chrome: black header + ruby gradient underline, light body. */
const RubyModal = ({ icon: Icon, title, subtitle, onClose, widthClass = 'max-w-2xl', children }: {
  icon: React.FC<any>; title: string; subtitle?: string; onClose: () => void; widthClass?: string; children: React.ReactNode;
}) => (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div
      className={`bg-white rounded-2xl shadow-2xl w-full ${widthClass} max-h-[85vh] flex flex-col overflow-hidden`}
      onClick={e => e.stopPropagation()}
    >
      <div className="bg-black shrink-0">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center shrink-0">
              <Icon size={15} className="text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-white truncate">{title}</h3>
              {subtitle && <p className="text-[11px] text-gray-400">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10"
          >
            <X size={15} />
          </button>
        </div>
        <div style={{ background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' }} />
      </div>
      {children}
    </div>
  </div>
);

const MenusPage = () => {
  const productConfig = getAdminProductConfig();
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [productName, setProductName] = useState<string>(productConfig.name);
  const [groups, setGroups] = useState<MenuGroup[]>([]);
  /*
    Read-only: the OTHER line's items that also show on this menu because their
    package is Shared (or this line's). Composed by super-admin-ext
    (lib/lineMenu.ts); edited only on the menu they belong to.
  */
  const [borrowedGroups, setBorrowedGroups] = useState<(MenuGroup & { borrowedFrom?: string })[]>([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [packages, setPackages] = useState<PackageOption[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [routeRegistry, setRouteRegistry] = useState<RouteEntry[]>([]);
  const [showRouteManager, setShowRouteManager] = useState(false);
  const [newRoutePath, setNewRoutePath] = useState('');
  const [newRouteLabel, setNewRouteLabel] = useState('');
  const [newRouteCategory, setNewRouteCategory] = useState('');
  const [expandedStyles, setExpandedStyles] = useState<Set<string>>(new Set());
  const [expandedAdvanced, setExpandedAdvanced] = useState<Set<string>>(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState<VersionHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [availableRoles, setAvailableRoles] = useState<string[]>(['admin', 'employee']);
  const [pendingConfirm, setPendingConfirm] = useState<
    { kind: 'reset' } | { kind: 'rollback'; version: number } | { kind: 'switchLine'; line: 'rubytill' | 'rubyestate' } | null
  >(null);
  const [line, setLine] = useState<'rubytill' | 'rubyestate'>('rubytill');
  const apiLine = line === 'rubyestate' ? 'rubyestate' as const : undefined;

  const requestLine = (next: 'rubytill' | 'rubyestate') => {
    if (next === line) return;
    if (!hasChanges) {
      setLine(next);
    } else {
      setPendingConfirm({ kind: 'switchLine', line: next });
    }
  };

  // DnD sensors with activation distance to avoid triggering on clicks
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const groupIds = useMemo(() => groups.map(g => g.id), [groups]);

  // Ruby Estate tab: packages on rubyestate/shared. Ruby Till tab: packages
  // missing a productLine (today's data), rubytill, or shared. No productLine
  // anywhere ⇒ every package shows on the Ruby Till tab, unchanged from today.
  const visiblePackages = useMemo(() => packages.filter(p => {
    if (line === 'rubyestate') return p.productLine === 'rubyestate' || p.productLine === 'shared';
    return !p.productLine || p.productLine === 'rubytill' || p.productLine === 'shared';
  }), [packages, line]);

  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = groups.findIndex(g => g.id === active.id);
    const newIndex = groups.findIndex(g => g.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(groups, oldIndex, newIndex);
    reordered.forEach((g, i) => g.displayOrder = i);
    setGroups(reordered);
    setHasChanges(true);
  };

  const handleItemDragEnd = (groupId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const items = [...(g.items || [])];
      const oldIndex = items.findIndex(i => i.id === active.id);
      const newIndex = items.findIndex(i => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return g;
      const reordered = arrayMove(items, oldIndex, newIndex);
      reordered.forEach((item, i) => item.displayOrder = i);
      return { ...g, items: reordered };
    }));
    setHasChanges(true);
  };

  // Load products and packages
  useEffect(() => {
    const load = async () => {
      try {
        const [productsResult, packagesResult] = await Promise.all([
          api.packages.listProducts(),
          api.packages.listVisible(),
        ]);
        const products = productsResult.products || [];
        if (products.length > 0) {
          // Always edit the platform-level product (the one the platform-app
          // reads from). Falls back to first product if not present.
          const platformId = productConfig.id;
          const platformProduct = products.find(p => p.id === platformId) || products[0];
          setSelectedProductId(platformProduct.id);
          setProductName(platformProduct.name || productConfig.name);
        }
        setPackages((packagesResult.packages || []).map((p: any) => ({ id: p.id, name: p.name, productLine: p.productLine })));
        // Extract unique roles from existing menu config
        const pkgList = packagesResult.packages || [];
        const roleSet = new Set<string>(['admin', 'employee']);
        pkgList.forEach((pkg: any) => {
          if (pkg.roles) pkg.roles.forEach((r: string) => roleSet.add(r));
        });
        setAvailableRoles(Array.from(roleSet));
      } catch (err: any) {
        setError('Failed to load products');
      }
    };
    load();
  }, []);

  // Load sidebar config when product changes
  useEffect(() => {
    if (!selectedProductId) return;
    loadConfig();
  }, [selectedProductId, line]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.sidebar.get(selectedProductId, apiLine);
      const config = result.sidebarConfig;
      setBorrowedGroups(Array.isArray((result as any).borrowedGroups) ? (result as any).borrowedGroups : []);
      if (config) {
        setGroups(config.groups || []);
        setVersion(config.version || 0);
        // Auto-seed route registry from defaults if empty
        const savedRegistry = config.routeRegistry || [];
        if (savedRegistry.length === 0) {
          setRouteRegistry(DEFAULT_ROUTE_REGISTRY);
          setHasChanges(true); // Mark dirty so user can save the auto-seeded registry
        } else {
          // Merge any new default routes not already in saved registry
          const existingPaths = new Set(savedRegistry.map((r: RouteEntry) => r.path));
          const merged = [...savedRegistry];
          for (const def of DEFAULT_ROUTE_REGISTRY) {
            if (!existingPaths.has(def.path)) merged.push(def);
          }
          setRouteRegistry(merged);
          if (merged.length > savedRegistry.length) setHasChanges(true);
        }
      } else {
        setGroups([]);
        setVersion(0);
        setRouteRegistry(DEFAULT_ROUTE_REGISTRY);
      }
      setHasChanges(false);
    } catch (err: any) {
      setError('Failed to load sidebar config');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedProductId) return;
    try {
      setSaving(true);
      setError(null);
      await api.sidebar.save(selectedProductId, {
        groups,
        routeRegistry,
        updatedBy: 'super_admin',
        version,
      }, apiLine);
      setVersion(v => v + 1);
      setHasChanges(false);
      setSuccessMsg('Sidebar config saved successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError('Failed to save sidebar config');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!selectedProductId) return;
    setPendingConfirm({ kind: 'reset' });
  };

  const doReset = async () => {
    if (!selectedProductId) return;
    try {
      setSaving(true);
      await api.sidebar.reset(selectedProductId, apiLine);
      setGroups([]);
      setVersion(0);
      setHasChanges(false);
      setSuccessMsg('Sidebar config reset to defaults');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError('Failed to reset sidebar config');
    } finally {
      setSaving(false);
    }
  };

  const toggleGroupExpanded = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });
  };

  // ========================
  // Group CRUD
  // ========================
  const addGroup = () => {
    const id = `group_${Date.now().toString(36)}`;
    const newGroup: MenuGroup = {
      id,
      label: 'New Group',
      displayOrder: groups.length,
      collapsible: true,
      defaultExpanded: true,
      roles: ['admin', 'employee'],
      items: [],
      subgroups: [],
    };
    setGroups(prev => [...prev, newGroup]);
    setExpandedGroups(prev => new Set(prev).add(id));
    setHasChanges(true);
  };

  const removeGroup = (groupId: string) => {
    setGroups(prev => prev.filter(g => g.id !== groupId));
    setHasChanges(true);
  };

  const updateGroup = (groupId: string, updates: Partial<MenuGroup>) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...updates } : g));
    setHasChanges(true);
  };

  const moveGroup = (index: number, direction: 'up' | 'down') => {
    const newGroups = [...groups];
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= newGroups.length) return;
    [newGroups[index], newGroups[targetIdx]] = [newGroups[targetIdx], newGroups[index]];
    newGroups.forEach((g, i) => g.displayOrder = i);
    setGroups(newGroups);
    setHasChanges(true);
  };

  // ========================
  // Menu Item CRUD
  // ========================
  const addItem = (groupId: string) => {
    const itemId = `item_${Date.now().toString(36)}`;
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const items = [...(g.items || [])];
      items.push({
        id: itemId,
        label: 'New Item',
        icon: 'Package',
        route: '/',
        displayOrder: items.length,
        roles: ['admin', 'employee'],
      });
      return { ...g, items };
    }));
    setHasChanges(true);
  };

  const removeItem = (groupId: string, itemId: string) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      return { ...g, items: (g.items || []).filter(i => i.id !== itemId) };
    }));
    setHasChanges(true);
  };

  const updateItem = (groupId: string, itemId: string, updates: Partial<MenuItem>) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      return {
        ...g,
        items: (g.items || []).map(i => i.id === itemId ? { ...i, ...updates } : i),
      };
    }));
    setHasChanges(true);
  };

  const moveItem = (groupId: string, index: number, direction: 'up' | 'down') => {
    setGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const items = [...(g.items || [])];
      const targetIdx = direction === 'up' ? index - 1 : index + 1;
      if (targetIdx < 0 || targetIdx >= items.length) return g;
      [items[index], items[targetIdx]] = [items[targetIdx], items[index]];
      items.forEach((item, i) => item.displayOrder = i);
      return { ...g, items };
    }));
    setHasChanges(true);
  };

  // ========================
  // Subgroup CRUD
  // ========================
  const addSubgroup = (parentId: string) => {
    const subId = `sub_${Date.now().toString(36)}`;
    setGroups(prev => prev.map(g => {
      if (g.id !== parentId) return g;
      return {
        ...g,
        subgroups: [...(g.subgroups || []), {
          id: subId,
          label: 'New Subgroup',
          displayOrder: (g.subgroups || []).length,
          collapsible: false,
          defaultExpanded: true,
          roles: ['admin', 'employee'],
          items: [],
        }],
      };
    }));
    setHasChanges(true);
  };

  const removeSubgroup = (parentId: string, subId: string) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== parentId) return g;
      return { ...g, subgroups: (g.subgroups || []).filter(s => s.id !== subId) };
    }));
    setHasChanges(true);
  };

  const updateSubgroup = (parentId: string, subId: string, updates: Partial<MenuGroup>) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== parentId) return g;
      return {
        ...g,
        subgroups: (g.subgroups || []).map(s => s.id === subId ? { ...s, ...updates } : s),
      };
    }));
    setHasChanges(true);
  };

  // Subgroup item CRUD
  const addSubgroupItem = (parentId: string, subId: string) => {
    const itemId = `item_${Date.now().toString(36)}`;
    setGroups(prev => prev.map(g => {
      if (g.id !== parentId) return g;
      return {
        ...g,
        subgroups: (g.subgroups || []).map(s => {
          if (s.id !== subId) return s;
          const items = [...(s.items || [])];
          items.push({
            id: itemId,
            label: 'New Item',
            icon: 'Package',
            route: '/',
            displayOrder: items.length,
            roles: ['admin', 'employee'],
          });
          return { ...s, items };
        }),
      };
    }));
    setHasChanges(true);
  };

  const removeSubgroupItem = (parentId: string, subId: string, itemId: string) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== parentId) return g;
      return {
        ...g,
        subgroups: (g.subgroups || []).map(s => {
          if (s.id !== subId) return s;
          return { ...s, items: (s.items || []).filter(i => i.id !== itemId) };
        }),
      };
    }));
    setHasChanges(true);
  };

  const updateSubgroupItem = (parentId: string, subId: string, itemId: string, updates: Partial<MenuItem>) => {
    setGroups(prev => prev.map(g => {
      if (g.id !== parentId) return g;
      return {
        ...g,
        subgroups: (g.subgroups || []).map(s => {
          if (s.id !== subId) return s;
          return {
            ...s,
            items: (s.items || []).map(i => i.id === itemId ? { ...i, ...updates } : i),
          };
        }),
      };
    }));
    setHasChanges(true);
  };

  // ========================
  // Route Registry
  // ========================
  const addRoute = () => {
    if (!newRoutePath || !newRouteLabel) return;
    const path = newRoutePath.startsWith('/') ? newRoutePath : `/${newRoutePath}`;
    if (routeRegistry.some(r => r.path === path)) return;
    setRouteRegistry(prev => [...prev, { path, label: newRouteLabel, category: newRouteCategory || 'Other' }]);
    setNewRoutePath('');
    setNewRouteLabel('');
    setNewRouteCategory('');
    setHasChanges(true);
  };

  const removeRoute = (path: string) => {
    setRouteRegistry(prev => prev.filter(r => r.path !== path));
    setHasChanges(true);
  };

  // Build grouped routes for the dropdown — combine registry + any routes used by items but not in registry
  const getGroupedRoutes = () => {
    const allRoutes = new Map<string, RouteEntry>();
    routeRegistry.forEach(r => allRoutes.set(r.path, r));
    // Also collect routes from existing items
    const collectFromItems = (items: MenuItem[]) => {
      items.forEach(item => {
        if (item.route && !allRoutes.has(item.route)) {
          allRoutes.set(item.route, { path: item.route, label: item.label, category: 'In Use' });
        }
      });
    };
    groups.forEach(g => {
      collectFromItems(g.items || []);
      (g.subgroups || []).forEach(s => collectFromItems(s.items || []));
    });

    const grouped: Record<string, RouteEntry[]> = {};
    allRoutes.forEach(r => {
      const cat = r.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(r);
    });
    return grouped;
  };

  const routeCategories = ROUTE_CATEGORY_ORDER.filter(c => c !== 'In Use');

  // ========================
  // Version History
  // ========================
  const loadHistory = async () => {
    if (!selectedProductId) return;
    try {
      setLoadingHistory(true);
      const result = await api.sidebar.getHistory(selectedProductId, apiLine);
      setVersionHistory(result.history || []);
    } catch (err: any) {
      setError('Failed to load version history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRollback = (targetVersion: number) => {
    setPendingConfirm({ kind: 'rollback', version: targetVersion });
  };

  const doRollback = async (targetVersion: number) => {
    try {
      setSaving(true);
      await api.sidebar.rollback(selectedProductId, targetVersion, apiLine);
      await loadConfig();
      setShowHistory(false);
      setSuccessMsg(`Rolled back to version ${targetVersion}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError('Failed to rollback: ' + (err.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const toggleAdvancedExpanded = (itemId: string) => {
    setExpandedAdvanced(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  // ========================
  // Menu Style Presets
  // ========================
  const GRADIENT_PRESETS = [
    { label: 'Purple-Pink', from: '#e8365a', to: '#ec4899' },
    { label: 'Blue-Cyan', from: '#3b82f6', to: '#06b6d4' },
    { label: 'Green-Teal', from: '#22c55e', to: '#14b8a6' },
    { label: 'Orange-Red', from: '#f97316', to: '#ef4444' },
    { label: 'Amber-Yellow', from: '#f59e0b', to: '#eab308' },
    { label: 'Rose-Purple', from: '#f43f5e', to: '#e8365a' },
    { label: 'Indigo-Blue', from: '#6366f1', to: '#3b82f6' },
    { label: 'Emerald-Lime', from: '#10b981', to: '#84cc16' },
    { label: 'Fuchsia-Violet', from: '#d946ef', to: '#e8365a' },
    { label: 'Sky-Indigo', from: '#0ea5e9', to: '#6366f1' },
  ];

  const SOLID_COLORS = [
    '#e8365a', '#ec4899', '#3b82f6', '#06b6d4', '#22c55e',
    '#14b8a6', '#f97316', '#ef4444', '#f59e0b', '#eab308',
    '#f43f5e', '#6366f1', '#e8365a', '#d946ef', '#0ea5e9',
    '#10b981', '#84cc16', '#64748b', '#78716c', '#ffffff',
  ];

  const toggleStyleExpanded = (itemId: string) => {
    setExpandedStyles(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  // ========================
  // Render a single menu item row
  // ========================
  const renderItemRow = (
    item: MenuItem,
    index: number,
    totalItems: number,
    onUpdate: (itemId: string, updates: Partial<MenuItem>) => void,
    onRemove: (itemId: string) => void,
    onMove: (index: number, direction: 'up' | 'down') => void,
    dragHandleProps?: { listeners?: any; attributes?: any },
  ) => {
    const ItemIcon = getIcon(item.icon);
    const styleType = item.menuStyle?.type || 'default';
    const isStyleExpanded = expandedStyles.has(item.id);
    const isAdvancedExpanded = expandedAdvanced.has(item.id);
    const advancedCount =
      (item.visibilityRules || []).length + (item.badgeSource ? 1 : 0) + Object.keys(item.labels || {}).length;
    const filledIcon = styleType === 'gradient' || styleType === 'solid';

    // Route options: category headers (level 0 __group_) and routes (level 1)
    const grouped = getGroupedRoutes();
    const orderedCats = [
      ...ROUTE_CATEGORY_ORDER.filter(c => grouped[c]),
      ...Object.keys(grouped).filter(c => !ROUTE_CATEGORY_ORDER.includes(c)),
    ];
    const allPaths = Object.values(grouped).flat().map(r => r.path);
    const isCustom = item.route && !allPaths.includes(item.route);
    const routeOptions: { value: string; label: string; subtitle?: string; level?: number }[] = [];
    for (const cat of orderedCats) {
      routeOptions.push({ value: `__group_${cat}`, label: cat, level: 0 });
      for (const r of grouped[cat]) {
        routeOptions.push({ value: r.path, label: r.label, subtitle: r.path, level: 1 });
      }
    }
    if (isCustom && item.route) {
      routeOptions.push({ value: `__group_custom`, label: 'Custom', level: 0 });
      routeOptions.push({ value: item.route, label: `${item.route} (custom)`, level: 1 });
    }
    const packageOptions = [
      { value: '', label: 'No package required' },
      ...visiblePackages.map(pkg => ({ value: pkg.id, label: pkg.name })),
    ];

    const setStyle = (patch: Record<string, any>) =>
      onUpdate(item.id, { menuStyle: { ...(item.menuStyle || {}), ...patch } });

    return (
      <div key={item.id} className="bg-white rounded-xl border border-stone-200 shadow-sm hover:border-stone-300 transition-colors">
        <div className="p-2.5 space-y-2">
          {/* Row 1: drag · icon · label · icon picker · style · advanced · delete */}
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-stone-300 hover:text-stone-600 touch-none shrink-0"
              title="Drag to reorder"
              {...(dragHandleProps?.listeners || {})}
              {...(dragHandleProps?.attributes || {})}
            >
              <GripVertical size={14} />
            </div>

            <div
              className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center shrink-0"
              style={
                styleType === 'gradient'
                  ? { background: `linear-gradient(135deg, ${item.menuStyle?.gradientFrom || '#e8365a'}, ${item.menuStyle?.gradientTo || '#ec4899'})` }
                  : styleType === 'solid'
                    ? { backgroundColor: item.menuStyle?.color || '#e8365a' }
                    : styleType === 'accent'
                      ? { borderLeft: `3px solid ${item.menuStyle?.color || '#e8365a'}` }
                      : undefined
              }
            >
              <ItemIcon size={15} className={filledIcon ? 'text-white' : 'text-[#DC143C]'} />
            </div>

            <input
              value={item.label}
              onChange={(e) => onUpdate(item.id, { label: e.target.value })}
              placeholder="Label"
              aria-label="Menu item label"
              className={`${INPUT_CLASS} flex-1 min-w-0 font-medium`}
            />
            <IconPicker
              value={item.icon}
              onChange={(icon) => onUpdate(item.id, { icon })}
              variant="light"
              size="sm"
              className="w-40 shrink-0"
            />
            <IconButton
              title="Menu style"
              active={isStyleExpanded || styleType !== 'default'}
              onClick={() => toggleStyleExpanded(item.id)}
            >
              <Paintbrush size={14} />
            </IconButton>
            <IconButton
              title="Advanced settings"
              active={isAdvancedExpanded}
              badge={advancedCount}
              onClick={() => toggleAdvancedExpanded(item.id)}
            >
              <Shield size={14} />
            </IconButton>
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              title="Remove item"
              aria-label="Remove item"
              className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>

          {/* Row 2: route · package · roles */}
          <div className="flex flex-wrap items-end gap-2.5 pl-8">
            <div className="flex-1 min-w-[260px]">
              <FieldLabel>Route</FieldLabel>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <SearchableDropdown
                    options={routeOptions}
                    value={item.route || null}
                    onChange={(v) => onUpdate(item.id, { route: v || '' })}
                    placeholder="Select route..."
                    searchPlaceholder="Search routes..."
                    ariaLabel="Route"
                    variant="light"
                    compact
                    buttonMono
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowRouteManager(true)}
                  className="h-8 px-3 text-xs font-medium rounded-lg border border-stone-300 bg-white text-stone-600 hover:border-[#DC143C] hover:text-[#DC143C] transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
                  title="Manage Routes"
                >
                  <Link2 size={14} />
                  Manage
                </button>
              </div>
            </div>
            <div className="w-56">
              <FieldLabel>Requires package</FieldLabel>
              <SearchableDropdown
                options={packageOptions}
                value={item.requiredPackageId || ''}
                onChange={(v) => onUpdate(item.id, { requiredPackageId: v || undefined })}
                placeholder="No package required"
                searchPlaceholder="Search packages..."
                ariaLabel="Required package"
                variant="light"
                    compact
              />
            </div>
            <div>
              <FieldLabel>Visible to</FieldLabel>
              <RoleChips
                roles={availableRoles}
                selected={item.roles || []}
                onChange={(roles) => onUpdate(item.id, { roles })}
              />
            </div>
          </div>

          {/* Advanced: badge, i18n labels, visibility rules */}
          {isAdvancedExpanded && (
            <div className="ml-8 rounded-lg border border-stone-200 bg-stone-50/70 p-3 space-y-3">
              <PanelTitle icon={Shield}>Advanced settings</PanelTitle>

              <div>
                <FieldLabel icon={Bell}>Badge source</FieldLabel>
                <input
                  value={item.badgeSource || ''}
                  onChange={(e) => onUpdate(item.id, { badgeSource: e.target.value || undefined })}
                  placeholder="/api/endpoint/count"
                  className={`${INPUT_CLASS} font-mono`}
                />
              </div>

              <div>
                <FieldLabel icon={Globe}>Translated labels</FieldLabel>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {['en', 'es', 'fr', 'de', 'ja', 'zh', 'si'].map(lang => (
                    <div
                      key={lang}
                      className="flex items-center rounded-lg border border-stone-300 bg-white overflow-hidden focus-within:border-[#DC143C] focus-within:ring-2 focus-within:ring-[#DC143C]/15"
                    >
                      <span className="px-2.5 self-stretch flex items-center text-[10px] font-bold uppercase text-stone-500 bg-stone-50 border-r border-stone-200">
                        {lang}
                      </span>
                      <input
                        value={item.labels?.[lang] || ''}
                        onChange={(e) => {
                          const newLabels = { ...(item.labels || {}), [lang]: e.target.value };
                          if (!e.target.value) delete newLabels[lang];
                          onUpdate(item.id, { labels: Object.keys(newLabels).length > 0 ? newLabels : undefined });
                        }}
                        placeholder={lang === 'en' ? item.label : ''}
                        className="flex-1 min-w-0 h-8 px-2.5 bg-transparent text-sm text-stone-900 placeholder-stone-400 focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <FieldLabel icon={CalendarClock}>Visibility rules</FieldLabel>
                <div className="space-y-2">
                  {(item.visibilityRules || []).map((rule, rIdx) => {
                    const setRule = (patch: Partial<VisibilityRule>) => {
                      const rules = [...(item.visibilityRules || [])];
                      rules[rIdx] = { ...rule, ...patch };
                      onUpdate(item.id, { visibilityRules: rules });
                    };
                    return (
                      <div key={rIdx} className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 bg-white p-2">
                        <Segmented
                          options={[
                            { value: 'date', label: 'Date' },
                            { value: 'package', label: 'Package' },
                            { value: 'feature', label: 'Feature' },
                          ]}
                          value={rule.type}
                          onChange={(type) => setRule({ type })}
                        />
                        <Segmented
                          options={rule.type === 'date'
                            ? [{ value: 'before', label: 'Show before' }, { value: 'after', label: 'Show after' }]
                            : [{ value: 'has', label: 'Has' }, { value: 'missing', label: 'Missing' }]}
                          value={rule.condition}
                          onChange={(condition) => setRule({ condition })}
                        />
                        <div className="flex-1 min-w-[180px]">
                          {rule.type === 'date' ? (
                            <DatePicker
                              value={rule.value}
                              onChange={(value) => setRule({ value })}
                              placeholder="Pick a date"
                              className="!h-8 !text-[13px]"
                            />
                          ) : (
                            <input
                              value={rule.value}
                              onChange={(e) => setRule({ value: e.target.value })}
                              placeholder="package-id"
                              className={`${INPUT_CLASS} font-mono`}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const rules = (item.visibilityRules || []).filter((_, i) => i !== rIdx);
                            onUpdate(item.id, { visibilityRules: rules.length > 0 ? rules : undefined });
                          }}
                          aria-label="Remove rule"
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                  <AddButton
                    onClick={() => {
                      const rules = [...(item.visibilityRules || []), { type: 'date' as const, condition: 'after' as const, value: '' }];
                      onUpdate(item.id, { visibilityRules: rules });
                    }}
                  >
                    Add rule
                  </AddButton>
                </div>
              </div>
            </div>
          )}

          {/* Menu style */}
          {isStyleExpanded && (
            <div className="ml-8 rounded-lg border border-stone-200 bg-stone-50/70 p-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <PanelTitle icon={Paintbrush}>Menu style</PanelTitle>
                <Segmented
                  options={[
                    { value: 'default', label: 'Default' },
                    { value: 'gradient', label: 'Gradient' },
                    { value: 'solid', label: 'Solid' },
                    { value: 'accent', label: 'Accent' },
                  ]}
                  value={styleType as 'default' | 'gradient' | 'solid' | 'accent'}
                  onChange={(type) => setStyle({ type })}
                />
              </div>

              {styleType === 'gradient' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-4">
                    <ColorSwatchInput
                      label="From"
                      value={item.menuStyle?.gradientFrom || '#e8365a'}
                      onChange={(v) => setStyle({ type: 'gradient', gradientFrom: v })}
                    />
                    <ColorSwatchInput
                      label="To"
                      value={item.menuStyle?.gradientTo || '#ec4899'}
                      onChange={(v) => setStyle({ type: 'gradient', gradientTo: v })}
                    />
                    <div
                      className="flex-1 min-w-[120px] h-8 rounded-lg shadow-inner"
                      style={{ background: `linear-gradient(90deg, ${item.menuStyle?.gradientFrom || '#e8365a'}, ${item.menuStyle?.gradientTo || '#ec4899'})` }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {GRADIENT_PRESETS.map(preset => {
                      const on = item.menuStyle?.gradientFrom === preset.from && item.menuStyle?.gradientTo === preset.to;
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => setStyle({ type: 'gradient', gradientFrom: preset.from, gradientTo: preset.to })}
                          title={preset.label}
                          aria-label={preset.label}
                          className={`w-10 h-7 rounded-lg transition-shadow ${on ? 'ring-2 ring-offset-2 ring-[#DC143C]' : 'hover:ring-2 hover:ring-stone-300'}`}
                          style={{ background: `linear-gradient(90deg, ${preset.from}, ${preset.to})` }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {(styleType === 'solid' || styleType === 'accent') && (
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <ColorSwatchInput
                      label="Color"
                      value={item.menuStyle?.color || '#e8365a'}
                      onChange={(v) => setStyle({ type: styleType, color: v })}
                    />
                    {styleType === 'accent' && (
                      <div className="flex items-center gap-2 h-8 px-3 rounded-lg bg-white border border-stone-200"
                        style={{ borderLeft: `3px solid ${item.menuStyle?.color || '#e8365a'}` }}>
                        <span className="text-xs text-stone-600">{item.label || 'Preview'}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SOLID_COLORS.map((color, cIdx) => (
                      <button
                        key={`${color}-${cIdx}`}
                        type="button"
                        onClick={() => setStyle({ type: styleType, color })}
                        aria-label={color}
                        className={`w-8 h-8 rounded-lg border border-stone-200 transition-shadow ${
                          item.menuStyle?.color === color ? 'ring-2 ring-offset-2 ring-[#DC143C]' : 'hover:ring-2 hover:ring-stone-300'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {styleType !== 'default' && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="text-xs font-medium text-stone-500 w-10">Font</span>
                  <Segmented
                    options={[
                      { value: 'normal', label: 'Normal' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'semibold', label: 'Semibold' },
                      { value: 'bold', label: 'Bold' },
                    ]}
                    value={item.menuStyle?.fontWeight || 'normal'}
                    onChange={(fontWeight) => setStyle({ fontWeight })}
                  />
                  <Segmented
                    options={[
                      { value: 'xs', label: 'XS' },
                      { value: 'sm', label: 'Small' },
                      { value: 'base', label: 'Base' },
                    ]}
                    value={item.menuStyle?.fontSize || 'sm'}
                    onChange={(fontSize) => setStyle({ fontSize })}
                  />
                  <Segmented
                    options={[
                      { value: 'none', label: 'Normal case' },
                      { value: 'uppercase', label: 'UPPERCASE' },
                      { value: 'capitalize', label: 'Capitalize' },
                    ]}
                    value={item.menuStyle?.textTransform || 'none'}
                    onChange={(textTransform) => setStyle({ textTransform })}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const totalItems = groups.reduce(
    (n, g) => n + (g.items || []).length + (g.subgroups || []).reduce((m, s) => m + (s.items || []).length, 0),
    0,
  );
  const allExpanded = groups.length > 0 && groups.every(g => expandedGroups.has(g.id));

  return (
    <StaffPage
      bodyClassName="bg-stone-50"
      icon={LayoutList}
      section="Platform"
      title="Menu Configuration"
      subtitle={`Sidebar groups and items · ${productName}`}
      actions={(
        <>
          <button
            onClick={() => { setShowHistory(true); loadHistory(); }}
            className="flex items-center gap-1.5 px-3 rounded-lg border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-colors min-h-[38px]"
            title="Version History"
          >
            <History size={15} />
            <span className="hidden sm:inline">History</span>
          </button>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-1.5 px-3 rounded-lg text-sm font-medium transition-colors min-h-[38px]"
            style={showPreview
              ? { background: 'rgba(220,20,60,0.16)', border: '1px solid rgba(220,20,60,0.5)', color: '#fda4af' }
              : { border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
          >
            <Eye size={15} />
            <span className="hidden sm:inline">Preview</span>
          </button>
          <button
            onClick={handleReset}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 rounded-lg border border-white/20 text-white/80 text-sm font-medium hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50 min-h-[38px]"
          >
            <RotateCcw size={15} />
            <span className="hidden sm:inline">Reset</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-5 rounded-lg font-semibold text-sm transition-all min-h-[38px] ${
              hasChanges ? 'text-white shadow-lg shadow-rose-900/40 hover:brightness-110' : 'bg-white/10 text-white/40 cursor-not-allowed'
            }`}
            style={hasChanges ? { background: RUBY_GRADIENT } : undefined}
          >
            {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
            <span>Save</span>
          </button>
        </>
      )}
    >
      <div className={`${showPreview ? 'max-w-7xl' : 'max-w-5xl'} mx-auto`}>

      <div role="tablist" aria-label="Product line" className="inline-flex gap-1.5 rounded-full border border-stone-200 bg-white p-1 mb-4">
        {([['rubytill', 'Ruby Till'], ['rubyestate', 'Ruby Estate']] as const).map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={line === id}
            onClick={() => requestLine(id)}
            className={`min-h-[44px] rounded-full px-4 text-sm font-semibold ${line === id ? 'bg-[#DC143C] text-white' : 'text-zinc-700 hover:bg-stone-100'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-2 text-red-600">
          <AlertCircle size={14} />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center space-x-2 text-emerald-700">
          <Check size={14} />
          <span className="text-sm">{successMsg}</span>
        </div>
      )}

      {loading ? (
        <RubyLoader variant="page" />
      ) : (
        <div className="flex gap-6 items-start">
          {/* Menu Groups Editor */}
          <div className={`${showPreview ? 'w-2/3' : 'w-full'} space-y-3 min-w-0`}>
            {/* Summary toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-stone-200 rounded-xl px-3 py-2 shadow-sm">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-stone-900">{groups.length} group{groups.length !== 1 ? 's' : ''}</span>
                <span className="text-stone-300">&middot;</span>
                <span className="text-stone-600">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                <span className="text-stone-300">&middot;</span>
                <span className="text-stone-500">v{version}</span>
                {hasChanges && (
                  <span className="ml-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#9F1239] bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]" />
                    Unsaved changes
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExpandedGroups(allExpanded ? new Set() : new Set(groups.map(g => g.id)))}
                  disabled={groups.length === 0}
                  className="h-8 px-3 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-900 inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  <ChevronsUpDown size={15} />
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRouteManager(true)}
                  className="h-8 px-3 rounded-lg border border-stone-300 text-sm font-medium text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C] inline-flex items-center gap-1.5 transition-colors"
                >
                  <Link2 size={15} />
                  Manage routes
                </button>
              </div>
            </div>

            {/* Groups */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGroupDragEnd}>
              <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
            {groups.map((group, index) => {
              const isExpanded = expandedGroups.has(group.id);
              const itemCount = (group.items || []).length;
              const subCount = (group.subgroups || []).length;
              const GroupIcon = group.icon ? getIcon(group.icon) : LayoutList;
              return (
                <SortableGroupCard key={group.id} group={group} index={index} renderContent={(groupDragHandle) => (
                <div className={`bg-white border rounded-2xl overflow-hidden shadow-sm transition-colors ${
                  isExpanded ? 'border-stone-300' : 'border-stone-200 hover:border-stone-300'
                }`}>
                  {/* Group Header */}
                  <div
                    className="flex items-center gap-2.5 px-3 py-2 cursor-pointer select-none"
                    onClick={() => toggleGroupExpanded(group.id)}
                  >
                    <div
                      className="w-6 h-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-stone-300 hover:text-stone-600 touch-none shrink-0"
                      title="Drag to reorder"
                      onClick={e => e.stopPropagation()}
                      {...groupDragHandle.listeners}
                      {...groupDragHandle.attributes}
                    >
                      <GripVertical size={15} />
                    </div>
                    <div
                      className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 text-white shadow-sm"
                      style={{ background: RUBY_GRADIENT }}
                    >
                      <GroupIcon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <input
                        value={group.label}
                        onChange={(e) => updateGroup(group.id, { label: e.target.value })}
                        onClick={e => e.stopPropagation()}
                        placeholder="Group name"
                        aria-label="Group name"
                        className="w-full max-w-sm bg-transparent rounded-md px-2 -mx-2 py-0.5 text-sm font-semibold text-stone-900 placeholder-stone-400 hover:bg-stone-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#DC143C]/20"
                      />
                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        <span className="text-[11px] font-semibold text-[#9F1239] bg-rose-50 rounded-full px-2 py-0.5">
                          {itemCount} item{itemCount !== 1 ? 's' : ''}
                        </span>
                        {subCount > 0 && (
                          <span className="text-[11px] font-semibold text-stone-600 bg-stone-100 rounded-full px-2 py-0.5">
                            {subCount} subgroup{subCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="text-[11px] font-medium text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">
                          {group.collapsible ? 'Collapsible' : 'Always open'}
                        </span>
                        <span className="text-[11px] text-stone-400 font-mono ml-1">{group.id}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeGroup(group.id); }}
                      title="Remove group"
                      aria-label="Remove group"
                      className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                    <span
                      className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                        isExpanded ? 'bg-rose-50 text-[#DC143C]' : 'bg-stone-100 text-stone-500'
                      }`}
                    >
                      <ChevronDown size={15} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </span>
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t border-stone-200">
                      {/* Group Settings */}
                      <div className="px-3 py-1.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 border-b border-stone-200 bg-stone-50">
                        <Toggle
                          checked={group.collapsible}
                          onChange={(collapsible) => updateGroup(group.id, { collapsible })}
                          label="Collapsible"
                          hint="Merchants can fold this group"
                        />
                        <Toggle
                          checked={group.defaultExpanded}
                          onChange={(defaultExpanded) => updateGroup(group.id, { defaultExpanded })}
                          label="Expanded by default"
                          hint="Starts open in the sidebar"
                        />
                        <div className="flex items-center gap-3 lg:ml-auto">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Visible to</span>
                          <RoleChips
                            roles={availableRoles}
                            selected={group.roles}
                            onChange={(roles) => updateGroup(group.id, { roles })}
                          />
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="p-3 space-y-2.5">
                        <SectionHead
                          title="Menu items"
                          count={itemCount}
                          action={<AddButton onClick={() => addItem(group.id)}>Add item</AddButton>}
                        />
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleItemDragEnd(group.id, e)}>
                          <SortableContext items={(group.items || []).map(i => i.id)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-2">
                        {(group.items || []).map((item, itemIdx) =>
                          <SortableItemRow
                            key={item.id}
                            item={item}
                            index={itemIdx}
                            totalItems={(group.items || []).length}
                            onUpdate={(itemId, updates) => updateItem(group.id, itemId, updates)}
                            onRemove={(itemId) => removeItem(group.id, itemId)}
                            onMove={(idx, dir) => moveItem(group.id, idx, dir)}
                            renderItemRow={renderItemRow}
                          />
                        )}
                            </div>
                          </SortableContext>
                        </DndContext>
                        {(group.items || []).length === 0 && (
                          <div className="rounded-xl border-2 border-dashed border-stone-200 py-6 text-center">
                            <p className="text-sm text-stone-500">No items yet. Click <span className="font-semibold text-[#DC143C]">Add item</span> to create menu entries.</p>
                          </div>
                        )}
                      </div>

                      {/* Subgroups */}
                      <div className="px-3 pb-3 pt-2.5 border-t border-stone-100 space-y-2.5">
                        <SectionHead
                          title="Subgroups"
                          count={subCount}
                          action={<AddButton onClick={() => addSubgroup(group.id)}>Add subgroup</AddButton>}
                        />
                        {(group.subgroups || []).map(sub => (
                          <div key={sub.id} className="rounded-xl border border-stone-200 border-l-4 border-l-[#DC143C] bg-stone-50/60 p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="w-7 flex items-center justify-center text-stone-300 shrink-0">
                                <Layers size={14} />
                              </span>
                              <input
                                value={sub.label}
                                onChange={(e) => updateSubgroup(group.id, sub.id, { label: e.target.value })}
                                aria-label="Subgroup name"
                                className={`${INPUT_CLASS} max-w-xs font-medium`}
                              />
                              <span className="text-[11px] font-semibold text-stone-500 bg-white border border-stone-200 rounded-full px-2 py-0.5">
                                {(sub.items || []).length} item{(sub.items || []).length !== 1 ? 's' : ''}
                              </span>
                              <div className="ml-auto flex items-center gap-2">
                                <AddButton onClick={() => addSubgroupItem(group.id, sub.id)}>Item</AddButton>
                                <button
                                  type="button"
                                  onClick={() => removeSubgroup(group.id, sub.id)}
                                  title="Remove subgroup"
                                  aria-label="Remove subgroup"
                                  className="w-8 h-8 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            {(sub.items || []).map((item, itemIdx) =>
                              renderItemRow(
                                item,
                                itemIdx,
                                (sub.items || []).length,
                                (itemId, updates) => updateSubgroupItem(group.id, sub.id, itemId, updates),
                                (itemId) => removeSubgroupItem(group.id, sub.id, itemId),
                                () => {}, // no reorder for subgroup items for now
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                )} />
              );
            })}
              </SortableContext>
            </DndContext>

            {/* Add Group Button */}
            <button
              onClick={addGroup}
              className="w-full flex items-center justify-center gap-2 min-h-[44px] p-3 border-2 border-dashed border-stone-300 rounded-2xl text-stone-500 bg-white/60 hover:text-[#DC143C] hover:border-[#DC143C] hover:bg-rose-50/50 transition-colors"
            >
              <Plus size={15} />
              <span className="text-sm font-semibold">Add menu group</span>
            </button>

            {/* Also on this menu — from the other line, because the package is Shared. */}
            {borrowedGroups.length > 0 && (() => {
              const other = line === 'rubytill' ? 'rubyestate' : 'rubytill';
              const otherName = other === 'rubyestate' ? 'Ruby Estate' : 'Ruby Till';
              const pkgName = (id?: string) => packages.find((p) => p.id === id)?.name || id || '';
              const count = borrowedGroups.reduce((n, g) => n + (g.items?.length || 0), 0);
              return (
                <section aria-label={`Also on this menu from ${otherName}`} className="mt-2 rounded-2xl border border-stone-200 bg-white p-4 space-y-3 text-stone-900">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-stone-900">Also on this menu — from {otherName}</h3>
                      <p className="text-[12px] text-stone-600">
                        {count} item{count === 1 ? '' : 's'} whose package is marked Shared (or {line === 'rubyestate' ? 'Ruby Estate' : 'Ruby Till'}), so merchants see them here too. Edit them on the {otherName} menu.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => requestLine(other)}
                      className="min-h-[44px] px-4 rounded-lg border border-stone-300 text-sm font-semibold text-stone-700 hover:border-[#DC143C] hover:text-[#DC143C]"
                    >
                      Edit on {otherName}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {borrowedGroups.map((g) => (
                      <div key={g.id} className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-3">
                        <div className="text-[13px] font-semibold text-stone-800 mb-1.5">{g.label}</div>
                        <ul className="space-y-1">
                          {(g.items || []).map((i) => (
                            <li key={i.id} className="flex items-center gap-2 text-[12.5px] text-stone-700 min-w-0">
                              <span className="font-medium truncate">{i.label}</span>
                              <span className="font-mono text-[11px] text-stone-500 truncate">{i.route}</span>
                              {i.requiredPackageId && <span className="ml-auto shrink-0 text-[10.5px] px-1.5 py-0.5 rounded bg-white border border-stone-200 text-stone-600">{pkgName(i.requiredPackageId)}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })()}
          </div>

          {/* Route Manager Modal */}
          {showRouteManager && (
            <RubyModal
              icon={Link2}
              title="Route Registry"
              subtitle={`${routeRegistry.length} registered route${routeRegistry.length !== 1 ? 's' : ''} for the route picker`}
              onClose={() => setShowRouteManager(false)}
            >
              {/* Add new route */}
              <div className="p-5 border-b border-stone-200 bg-stone-50 shrink-0">
                <SectionHead title="Add new route" />
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_180px_auto] gap-2 items-end">
                  <div>
                    <FieldLabel>Path</FieldLabel>
                    <input
                      value={newRoutePath}
                      onChange={(e) => setNewRoutePath(e.target.value)}
                      placeholder="/route-path"
                      className={`${INPUT_CLASS} font-mono`}
                    />
                  </div>
                  <div>
                    <FieldLabel>Label</FieldLabel>
                    <input
                      value={newRouteLabel}
                      onChange={(e) => setNewRouteLabel(e.target.value)}
                      placeholder="Display label"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <FieldLabel>Category</FieldLabel>
                    <SearchableDropdown
                      options={routeCategories.map(c => ({ value: c, label: c }))}
                      value={newRouteCategory || null}
                      onChange={(v) => setNewRouteCategory(v || '')}
                      placeholder="Category"
                      searchPlaceholder="Search categories..."
                      ariaLabel="Route category"
                      variant="light"
                    compact
                    />
                  </div>
                  <button
                    onClick={addRoute}
                    disabled={!newRoutePath || !newRouteLabel}
                    className="h-8 px-5 text-white text-sm font-semibold rounded-lg inline-flex items-center justify-center gap-1.5 hover:brightness-110 disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ background: RUBY_GRADIENT }}
                  >
                    <Plus size={13} />
                    Add
                  </button>
                </div>
              </div>
              {/* Existing routes */}
              <div className="p-5 overflow-y-auto flex-1 space-y-5">
                {(() => {
                  const grouped = getGroupedRoutes();
                  return Object.entries(grouped).map(([cat, routes]) => (
                    <div key={cat}>
                      <SectionHead title={cat} count={routes.length} />
                      <div className="mt-2 rounded-xl border border-stone-200 divide-y divide-stone-100 overflow-hidden">
                        {routes.map(r => (
                          <div key={r.path} className="flex items-center justify-between gap-3 pl-3 pr-1 min-h-[38px] bg-white hover:bg-stone-50">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-sm font-medium text-stone-900 truncate">{r.label}</span>
                              <code className="text-[11px] font-mono text-[#9F1239] bg-rose-50 rounded-md px-1.5 py-0.5 truncate">{r.path}</code>
                            </div>
                            <button
                              onClick={() => removeRoute(r.path)}
                              title="Remove route"
                              aria-label={`Remove ${r.path}`}
                              className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
                {routeRegistry.length === 0 && (
                  <p className="text-sm text-stone-500 text-center py-8">No routes registered yet. Add routes above to populate the route picker.</p>
                )}
              </div>
            </RubyModal>
          )}

          {/* Preview Panel */}
          {showPreview && (
            <div className="w-1/3 min-w-0 sticky top-6">
              <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="px-4 py-2 border-b border-stone-200 flex items-center justify-between gap-2">
                  <SectionHead title="Sidebar preview" />
                  <div className="inline-flex rounded-lg bg-stone-100 p-1 gap-1">
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      className={`w-7 h-7 rounded flex items-center justify-center ${previewMode === 'desktop' ? 'bg-white text-[#DC143C] shadow-sm' : 'text-stone-500 hover:text-stone-800'}`}
                      title="Desktop view"
                    >
                      <Eye size={15} />
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      className={`w-7 h-7 rounded flex items-center justify-center ${previewMode === 'mobile' ? 'bg-white text-[#DC143C] shadow-sm' : 'text-stone-500 hover:text-stone-800'}`}
                      title="Mobile view"
                    >
                      <Smartphone size={15} />
                    </button>
                  </div>
                </div>
                {/* The merchant sidebar is black — the preview stays black to match it */}
                <div className={previewMode === 'mobile' ? 'flex justify-center p-4 bg-stone-100' : 'bg-black'}>
                  <div className={previewMode === 'mobile' ? 'w-[280px] border border-white/20 rounded-[28px] p-3 bg-black shadow-2xl' : ''}>
                    {previewMode === 'mobile' && (
                      <div className="flex justify-center mb-2">
                        <div className="w-16 h-1 bg-white/20 rounded-full" />
                      </div>
                    )}
                    <div className={`space-y-1 overflow-y-auto ${previewMode === 'mobile' ? 'max-h-[480px] px-1' : 'p-3 max-h-[600px]'}`}>
                      {groups.map(group => {
                        const itemSize = previewMode === 'mobile' ? 14 : 16;
                        return (
                          <div key={group.id}>
                            {group.collapsible ? (
                              <div className={`pt-4 pb-1 px-3 flex items-center justify-between`}>
                                <p className={`${previewMode === 'mobile' ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-widest text-white/40 font-bold`}>{group.label}</p>
                                <ChevronDown size={previewMode === 'mobile' ? 10 : 12} className="text-white/30" />
                              </div>
                            ) : (
                              <div className="pt-4 pb-1 px-3">
                                <p className={`${previewMode === 'mobile' ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-widest text-white/40 font-bold`}>{group.label}</p>
                              </div>
                            )}
                            {(group.items || []).map(item => {
                              const ItemIcon = getIcon(item.icon);
                              const st = item.menuStyle;
                              const sType = st?.type || 'default';
                              const textStyle: React.CSSProperties =
                                sType === 'gradient'
                                  ? { backgroundImage: `linear-gradient(90deg, ${st?.gradientFrom || '#e8365a'}, ${st?.gradientTo || '#ec4899'})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: st?.fontWeight, fontSize: st?.fontSize === 'base' ? 16 : st?.fontSize === 'xs' ? 11 : undefined, textTransform: st?.textTransform as any }
                                  : sType === 'solid'
                                    ? { color: st?.color || '#e8365a', fontWeight: st?.fontWeight, fontSize: st?.fontSize === 'base' ? 16 : st?.fontSize === 'xs' ? 11 : undefined, textTransform: st?.textTransform as any }
                                    : {};
                              return (
                                <div
                                  key={item.id}
                                  className={`flex items-center space-x-2 px-3 ${previewMode === 'mobile' ? 'py-2.5' : 'py-2'} rounded-xl text-white/60 ${previewMode === 'mobile' ? 'text-xs' : 'text-sm'} hover:bg-white/5`}
                                  style={sType === 'accent' ? { borderLeft: `3px solid ${st?.color || '#e8365a'}` } : undefined}
                                >
                                  <ItemIcon size={itemSize} className="text-white/40" />
                                  <span style={textStyle} className="flex-1 truncate">{item.label}</span>
                                  {item.badgeSource && <span className="w-2 h-2 rounded-full bg-rose-500" />}
                                </div>
                              );
                            })}
                            {(group.subgroups || []).map(sub => (
                              <div key={sub.id} className="ml-2">
                                <div className="px-3 py-1 mt-2">
                                  <p className="text-[9px] uppercase tracking-widest text-white/25 font-bold">{sub.label}</p>
                                </div>
                                {(sub.items || []).map(item => {
                                  const SubIcon = getIcon(item.icon);
                                  return (
                                    <div key={item.id} className={`flex items-center space-x-2 px-3 ${previewMode === 'mobile' ? 'py-2.5' : 'py-2'} rounded-xl text-white/60 ${previewMode === 'mobile' ? 'text-xs' : 'text-sm'} hover:bg-white/5`}>
                                      <SubIcon size={itemSize} className="text-white/40" />
                                      <span className="truncate">{item.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                    {previewMode === 'mobile' && (
                      <div className="flex justify-center mt-2">
                        <div className="w-24 h-1 bg-white/10 rounded-full" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Version History Modal */}
      {showHistory && (
        <RubyModal
          icon={History}
          title="Version History"
          subtitle={`${productName} sidebar · restore any saved version`}
          onClose={() => setShowHistory(false)}
          widthClass="max-w-lg"
        >
          <div className="p-5 overflow-y-auto flex-1 space-y-3">
            <div className="p-4 rounded-xl border border-[#DC143C]/30 bg-rose-50/60 flex items-center justify-between">
              <div>
                <span className="text-sm text-stone-900 font-semibold">Current &mdash; v{version}</span>
                <span className="text-xs text-stone-500 ml-2">{groups.length} groups</span>
              </div>
              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">Active</span>
            </div>
            {loadingHistory ? (
              <RubyLoader variant="section" />
            ) : versionHistory.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-8">No previous versions yet. History is saved automatically when you save changes.</p>
            ) : (
              <div className="space-y-2">
                {versionHistory.map((entry, idx) => (
                  <div key={idx} className="p-3 bg-white rounded-xl border border-stone-200 hover:border-stone-300 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-xl bg-stone-100 text-stone-700 text-[11px] font-bold flex items-center justify-center shrink-0">
                        v{entry.version}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-stone-900">
                          {entry.groups?.length || 0} groups
                          <span className="text-stone-500"> &middot; by {entry.updatedBy}</span>
                        </p>
                        <p className="text-[11px] text-stone-500 mt-0.5">
                          {new Date(entry.updatedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRollback(entry.version)}
                      disabled={saving}
                      className="h-8 px-4 text-sm font-medium text-[#DC143C] rounded-lg border border-rose-200 bg-white hover:bg-rose-50 hover:border-[#DC143C] inline-flex items-center gap-1.5 disabled:opacity-40 shrink-0"
                    >
                      <RotateCcw size={14} />
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </RubyModal>
      )}

      {/* Reset / rollback confirmation — never window.confirm (no-op in the iOS webview) */}
      <ConfirmDialog
        open={!!pendingConfirm}
        title={
          pendingConfirm?.kind === 'reset' ? 'Reset sidebar config?'
          : pendingConfirm?.kind === 'switchLine' ? 'Discard unsaved menu changes?'
          : `Rollback to version ${pendingConfirm?.kind === 'rollback' ? pendingConfirm.version : ''}?`
        }
        tone={pendingConfirm?.kind === 'reset' || pendingConfirm?.kind === 'switchLine' ? 'danger' : 'default'}
        confirmLabel={pendingConfirm?.kind === 'reset' ? 'Reset to defaults' : pendingConfirm?.kind === 'switchLine' ? 'Discard changes' : 'Rollback'}
        busy={saving}
        onClose={() => setPendingConfirm(null)}
        onConfirm={() => {
          const action = pendingConfirm;
          setPendingConfirm(null);
          if (action?.kind === 'reset') doReset();
          else if (action?.kind === 'rollback') doRollback(action.version);
          else if (action?.kind === 'switchLine') setLine(action.line);
        }}
      >
        {pendingConfirm?.kind === 'reset'
          ? <p>Reset sidebar config to defaults? This will remove all custom menu groups.</p>
          : pendingConfirm?.kind === 'switchLine'
          ? <p>Discard unsaved menu changes? Switching product line will lose them.</p>
          : <p>Rollback to version {pendingConfirm?.kind === 'rollback' ? pendingConfirm.version : ''}? Current config will be archived.</p>}
      </ConfirmDialog>
      </div>
    </StaffPage>
  );
};

export default MenusPage;
